import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina } from '../components/layout/CabecalhoPagina';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, championshipApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { statusCampeonato } from '../lib/statusCampeonato';
import {
  Trophy,
  Users,
  Building2,
  Shield,
  TrendingUp,
  Calendar,
  ArrowRight,
  Loader2,
  LayoutDashboard,
  Medal,
} from 'lucide-react';

/**
 * ETAPA VISUAL 3 — DASHBOARD COM HIERARQUIA
 *
 * Antes: 4 KPIs IDÊNTICOS, cada um com a sua família de cor
 * (`from-blue-500`, `from-purple-500`, dourado, `from-green-500`) — a tela mais
 * colorida do sistema e a menos dourada, sem nenhum ponto de entrada. E os 3
 * atalhos eram cards grandes, disputando peso com os indicadores.
 *
 * Agora: 1 indicador PROTAGONISTA em card dourado (campeonatos ativos, com o
 * campeonato em destaque), 4 números secundários numa faixa discreta, e os
 * atalhos como BOTÕES de ação.
 *
 * Nenhum dado novo foi inventado: os números continuam vindo de
 * `/dashboard/stats` e a lista de `/championships?status=open`.
 */
export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, champsRes] = await Promise.all([
          dashboardApi.getStats(),
          championshipApi.list({ status: 'open' })
        ]);
        setStats(statsRes.data);
        setChampionships(champsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
        </div>
      </MainLayout>
    );
  }

  const primeiroNome = user?.name?.split(' ')[0] || '';
  const destaque = championships[0] || null;

  /* Os 4 números que antes eram 4 cards de mesmo peso agora são uma linha só. */
  const numerosSecundarios = [
    { rotulo: 'Atletas', valor: stats?.total_athletes || 0, icone: Users },
    { rotulo: 'Equipes', valor: stats?.total_teams || 0, icone: Shield },
    { rotulo: 'Federações', valor: stats?.total_federations || 0, icone: Building2 },
    { rotulo: 'Campeonatos', valor: stats?.total_championships || 0, icone: Trophy },
  ];

  /* Atalhos deixam de ser cards grandes e passam a ser botões de ação. */
  const atalhos = [
    { to: '/athletes/new', rotulo: 'Cadastrar atleta', icone: Users, testid: 'quick-new-athlete' },
    { to: '/registrations', rotulo: 'Inscrições', icone: TrendingUp, testid: 'quick-registrations' },
    { to: '/central-desk', rotulo: 'Mesa central', icone: Medal, testid: 'quick-central-desk' },
  ];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="dashboard">
        <CabecalhoPagina
          contexto="Visão geral"
          titulo={`Olá, ${primeiroNome}! 👋`}
          descricao="Bem-vindo ao sistema de gerenciamento de campeonatos"
          icone={LayoutDashboard}
          acoes={
            <Link
              to="/championships/new"
              data-testid="new-championship-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-bold rounded-lg hover:bg-[#E5C158] transition-colors"
            >
              <Trophy size={16} />
              Novo Campeonato
            </Link>
          }
        />

        {/* ============ 1 PROTAGONISTA + 4 NÚMEROS DISCRETOS ============ */}
        <div className="grid gap-4 lg:grid-cols-3">
          <Card
            className="relative overflow-hidden border-[#D4AF37]/35 bg-gradient-to-br from-[#D4AF37]/[0.14] via-[#141414] to-[#141414] lg:col-span-2"
            data-testid="stat-card-0"
          >
            <span
              className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#D4AF37]/40 to-transparent"
              aria-hidden="true"
            />
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="micro-rotulo">Campeonatos ativos</p>
                  <p className="mt-2 text-5xl font-bold text-white font-['Chivo'] tabular-nums leading-none">
                    {stats?.active_championships || 0}
                  </p>
                  <p className="mt-3 text-sm text-[#A1A1AA]">
                    {destaque ? (
                      <>
                        Próximo com inscrições abertas:{' '}
                        <span className="text-white font-medium">{destaque.name}</span>
                      </>
                    ) : (
                      'Nenhum campeonato com inscrições abertas neste momento'
                    )}
                  </p>
                </div>
                <span className="medalhao" aria-hidden="true">
                  <Trophy size={20} />
                </span>
              </div>

              {destaque && (
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span className={statusCampeonato(destaque.status).selo}>
                    {statusCampeonato(destaque.status).rotulo}
                  </span>
                  {destaque.date && (
                    <span className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
                      <Calendar size={12} aria-hidden="true" />
                      {destaque.date}
                    </span>
                  )}
                  <Link
                    to={`/championships/${destaque._id}`}
                    className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-[#D4AF37] hover:text-[#E5C158] transition-colors"
                  >
                    Abrir campeonato <ArrowRight size={16} />
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-[#27272A] bg-[#141414]" data-testid="numeros-secundarios">
            <CardContent className="grid h-full grid-cols-2 gap-x-4 gap-y-5 p-6">
              {numerosSecundarios.map((numero) => (
                <div key={numero.rotulo}>
                  <p className="micro-rotulo flex items-center gap-1.5">
                    <numero.icone size={12} aria-hidden="true" className="text-[#D4AF37]" />
                    {numero.rotulo}
                  </p>
                  <p className="mt-1.5 text-2xl font-bold text-white font-['Chivo'] tabular-nums">
                    {numero.valor}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* ============ AÇÕES RÁPIDAS (agora botões, não cards) ============ */}
        <div className="flex flex-wrap gap-2" data-testid="atalhos">
          {atalhos.map((atalho) => (
            <Link
              key={atalho.to}
              to={atalho.to}
              data-testid={atalho.testid}
              className="inline-flex items-center gap-2 rounded-lg border border-[#27272A] bg-[#141414] px-4 py-2.5 text-sm font-medium text-[#A1A1AA] transition-colors hover:border-[#D4AF37]/50 hover:text-white"
            >
              <atalho.icone size={16} aria-hidden="true" className="text-[#D4AF37]" />
              {atalho.rotulo}
            </Link>
          ))}
        </div>

        {/* ============ CAMPEONATOS COM INSCRIÇÕES ABERTAS ============ */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold text-white font-['Chivo'] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Campeonatos com Inscrições Abertas
            </CardTitle>
            <Link
              to="/championships"
              className="text-[#D4AF37] hover:text-[#E5C158] text-sm flex items-center gap-1"
              data-testid="view-all-championships"
            >
              Ver todos <ArrowRight size={16} />
            </Link>
          </CardHeader>
          <CardContent>
            {championships.length === 0 ? (
              <div className="text-center py-8 text-[#71717A]">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum campeonato com inscrições abertas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {championships.map((champ) => (
                  <Link
                    key={champ._id}
                    to={`/championships/${champ._id}`}
                    data-testid={`championship-${champ._id}`}
                    className="flex items-center justify-between gap-4 p-4 rounded-lg bg-[#1A1A1A] border border-[#27272A] hover:border-[#D4AF37]/50 transition-all"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="medalhao medalhao--suave medalhao--sm" aria-hidden="true">
                        <Trophy size={16} />
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{champ.name}</p>
                        <p className="text-sm text-[#71717A] truncate">
                          {[champ.date, champ.address].filter(Boolean).join(' • ')}
                        </p>
                      </div>
                    </div>
                    <span className={statusCampeonato(champ.status).selo}>
                      {statusCampeonato(champ.status).rotulo}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
